/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { View, BoxView, ActionButton, Pressable, Icon } from '@components/atoms'
import { Grid, settlementColumns, settlementYearColumns } from '@grid'
import {
	Checkbox,
	Select,
	SelectChangeEvent,
	MenuItem,
	FormControl,
	InputLabel,
	Tabs,
	Tab,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import {
	GridRenderCellParams,
	GridRowSelectionModel,
	GridRowModel,
} from '@mui/x-data-grid'
import { useAppDispatch, useAppSelector, ApiGetDataStatus } from '@redux/index'
import { settlementActions } from '@redux/store/index'
import { SettlementStateType } from '@redux/types'
import {
	SettlementRowType,
	SettlementMonthSummaryType,
	SettlementPatchType,
} from '@schemas'
import {
	boShared,
	boStatusChipClass,
	mono,
} from '@components/systems/backoffice/backoffice.tokens'
import { BoStatTiles, BoStatTile } from '@components/systems/backoffice/BoStatTiles'
import {
	SettlementUnit,
	SETTLEMENT_STATUS_TITLE,
	SETTLEMENT_STATUS_CHIP,
	SETTLEMENT_CHANNEL_TITLE,
	SettlementAggregate,
	aggregateRows,
	mergeSummaryKrw,
	totalRowKrw,
	toKrw,
	krw,
	originalMoney,
	weekStartOf,
	weekTitle,
} from '@components/systems/settlement/settlement.format'
import dayjs from 'dayjs'

const FIRST_YEAR = 2025
const UNITS: SettlementUnit[] = ['year', 'month', 'week']
const UNIT_TITLE: { [key in SettlementUnit]: string } = {
	year: '연간',
	month: '월간',
	week: '주간',
}

/**
 * 정산 (`/main/settlement`) — DISPATCH_PLAN §6. 독립 모듈, 지출(§7)과 서로 참조하지 않는다.
 * 2026-09-27 사용자 결정: **탭 셋(연간 · 월간 · 주간)**, 월간은 연간에서 들어가는 게 아니라 **월 피커**로, **전부 원화 환산**.
 *   연간 = 연도 셀렉트 → 큰 수 4 + 월별 집계 Grid(원화, 마지막 행 합계) — 월간·주간과 같은 Grid 컴포넌트.
 *   월간 = 월 피커 → 큰 수 4 + 그 달의 판매 행 Grid.
 *   주간 = 날짜 피커(그 주 월~일) · ‹ › → 큰 수 4 + 그 주의 판매 행 Grid.
 * 환율은 화면에서 바꾸지 않는다 — 주문이 만들어질 때 서버가 ORDER.USD_KRW_RATE 에 박고(FxRate), 행마다 `usdKrwRate` 로 온다.
 * 연간 집계의 원화 합도 서버가 그 환율로 계산한다(`*Krw`). USD 주문 셀은 툴팁에 원래 금액.
 * 탭은 `?tab=` 쿼리가 정본(replace). 체크 셋(부가세 납부 · 후원금 납부 · 세금계산서)은 누르는 즉시 저장되고 누가·언제가 남는다.
 * 납부 체크가 하나라도 있으면 그 주문의 값은 잠긴다 — 비용을 고치려면 먼저 체크를 푼다.
 */
const SettlementPage = () => {
	const reduxDispatch = useAppDispatch()
	const [searchParams, setSearchParams] = useSearchParams()

	const unit: SettlementStateType['unit'] = useAppSelector(state => state.settlement.unit)
	const year: string = useAppSelector(state => state.settlement.year)
	const month: string = useAppSelector(state => state.settlement.month)
	const week: string = useAppSelector(state => state.settlement.week)
	const summary: SettlementMonthSummaryType[] = useAppSelector(
		state => state.settlement.summary,
	)
	const summaryStatus: ApiGetDataStatus = useAppSelector(
		state => state.settlement.summaryStatus,
	)
	const rows: SettlementRowType[] = useAppSelector(state => state.settlement.rows)
	const rowsStatus: ApiGetDataStatus = useAppSelector(
		state => state.settlement.rowsStatus,
	)
	const selectedOrderIds: string[] = useAppSelector(
		state => state.settlement.selectedOrderIds,
	)
	const savingOrderIds: string[] = useAppSelector(
		state => state.settlement.savingOrderIds,
	)
	const bulkSaving: boolean = useAppSelector(state => state.settlement.bulkSaving)

	// 탭의 정본은 URL — `?tab=year|month|week`. 없으면 월간
	const rawTab = searchParams.get('tab') ?? ''
	const tab: SettlementUnit = (UNITS as string[]).includes(rawTab)
		? (rawTab as SettlementUnit)
		: 'month'
	const setTab = (next: SettlementUnit) => {
		const params = new URLSearchParams(searchParams)
		if (next === 'month') params.delete('tab')
		else params.set('tab', next)
		setSearchParams(params, { replace: true })
	}

	// 탭이 바뀌면 그 기간을 읽는다. 들어오면 반드시 한 번 읽는다(저장된 상태를 믿지 않는다 — Order.page 와 같은 이유)
	React.useEffect(() => {
		reduxDispatch(settlementActions.UNIT(tab))
		if (tab === 'year') reduxDispatch(settlementActions.YEAR(year))
	}, [tab, reduxDispatch])

	const years: string[] = React.useMemo(() => {
		const thisYear = dayjs().year()
		const list: string[] = []
		for (let y = thisYear; y >= FIRST_YEAR; y -= 1) list.push(String(y))
		if (!list.includes(year)) list.push(year)
		return list
	}, [year])

	const patch = (orderId: string, body: SettlementPatchType) =>
		reduxDispatch(settlementActions.TRIGGER_UPDATE({ orderId, body }))

	/** 수정 모드에서 고친 행 — 바뀐 칸만 보낸다(배송비 · 관세 · 계산서 번호 · 메모). 입력값은 주문 통화 그대로 */
	const onAction = (iudFlag: string, aRow: GridRowModel) => {
		if (iudFlag !== 'U') return
		const before = rows.find(r => r.orderId === aRow.orderId)
		if (before === undefined) return
		const body: SettlementPatchType = {}
		const toNum = (v: any): number | null =>
			v === null || v === undefined || v === '' || Number.isNaN(Number(v))
				? null
				: Number(v)
		if (toNum(aRow.shippingCost) !== before.shippingCost)
			body.shippingCost = toNum(aRow.shippingCost)
		if (toNum(aRow.dutyCost) !== before.dutyCost) body.dutyCost = toNum(aRow.dutyCost)
		if (String(aRow.taxInvoiceNo ?? '') !== before.taxInvoiceNo)
			body.taxInvoiceNo = String(aRow.taxInvoiceNo ?? '')
		if (String(aRow.memo ?? '') !== before.memo) body.memo = String(aRow.memo ?? '')
		if (Object.keys(body).length === 0) return
		patch(before.orderId, body)
	}

	const onBulk = (key: 'vatPaidYn' | 'donationPaidYn') => {
		if (selectedOrderIds.length === 0) return
		reduxDispatch(
			settlementActions.TRIGGER_BULK({ orderIds: selectedOrderIds, [key]: 1 }),
		)
	}

	/*
		선택한 행이 전부 입금 대기(도매 미입금)면 「입금 확인」, 전부 결제 확정이면 납부 처리 둘.
		섞이면 아무 버튼도 안 선다 — 입금 전 주문에 납부 체크를 할 수는 없다 (2026-09-27 사용자 결정: 입금은 정산에서).
	*/
	const selectedRows = React.useMemo(
		() => rows.filter(r => selectedOrderIds.includes(r.orderId)),
		[rows, selectedOrderIds],
	)
	const allAwaiting =
		selectedRows.length > 0 && selectedRows.every(r => r.status === 'awaitingPayment')
	const noneAwaiting =
		selectedRows.length > 0 && selectedRows.every(r => r.status !== 'awaitingPayment')
	const onMarkPaid = () => {
		if (!allAwaiting) return
		reduxDispatch(settlementActions.TRIGGER_MARK_PAID(selectedRows.map(r => r.orderId)))
	}

	/** 체크 칸 — 즉시 저장. 적립 대상이 아닌(환불·취소) 행의 납부 체크는 막는다 */
	const checkCell = (key: 'vatPaidYn' | 'donationPaidYn' | 'taxInvoiceYn') =>
		function SettlementCheckCell(
			params: GridRenderCellParams<SettlementRowType, any, any>,
		) {
			const row = params.row
			const saving = savingOrderIds.includes(row.orderId)
			// 입금 전(정산 행 없음)엔 아무 체크도 못 한다. 환불·취소 행은 납부 체크만 막는다
			const disabled =
				saving ||
				row.status === 'awaitingPayment' ||
				(key !== 'taxInvoiceYn' && row.eligibleYn === 0)
			const at =
				key === 'vatPaidYn'
					? row.vatPaidAt
					: key === 'donationPaidYn'
					? row.donationPaidAt
					: row.taxInvoiceAt
			const by =
				key === 'vatPaidYn'
					? row.vatPaidBy
					: key === 'donationPaidYn'
					? row.donationPaidBy
					: ''
			const title =
				row[key] === 1 && at !== ''
					? `${dayjs(at).format('YYYY-MM-DD HH:mm')}${by !== '' ? ` · ${by}` : ''}`
					: ''
			return (
				<Checkbox
					size="small"
					checked={row[key] === 1}
					disabled={disabled}
					title={title}
					onClick={e => e.stopPropagation()}
					onChange={e => patch(row.orderId, { [key]: e.target.checked ? 1 : 0 })}
				/>
			)
		}

	/** 금액 칸 — 원화 환산(그 주문의 시점 환율). USD 주문은 툴팁에 원래 금액 */
	const moneyCell = (params: GridRenderCellParams<SettlementRowType, any, any>) => (
		<span
			className="num"
			title={
				params.row.currency === 'USD'
					? `${originalMoney(
							'USD',
							params.value,
					  )} · ₩${params.row.usdKrwRate.toLocaleString('ko-KR')}/$`
					: undefined
			}
		>
			{krw(toKrw(params.row.currency, params.value, params.row.usdKrwRate))}
		</span>
	)
	/** 정산 값(원가·적립) — 정산 행이 없는 입금 대기 주문은 빈 대시 */
	const settledMoneyCell = (params: GridRenderCellParams<SettlementRowType, any, any>) =>
		params.row.hasSettlement ? moneyCell(params) : <span className="faint">—</span>

	// ── 기간 집계(원화) ──
	const monthsKrw = React.useMemo(() => mergeSummaryKrw(summary), [summary])
	const yearTotal = React.useMemo(() => totalRowKrw(monthsKrw), [monthsKrw])
	const yearRows = React.useMemo(
		() => (monthsKrw.length === 0 ? [] : [...monthsKrw, yearTotal]),
		[monthsKrw, yearTotal],
	)
	const rowsAggregate = React.useMemo(() => aggregateRows(rows), [rows])
	const aggregate: SettlementAggregate = tab === 'year' ? yearTotal : rowsAggregate

	const periodTitle =
		tab === 'year'
			? `${year}년`
			: tab === 'month'
			? dayjs(`${month}-01`).format('YYYY년 M월')
			: weekTitle(week)

	const tiles: BoStatTile[] = [
		{
			label: '판매 합계',
			value: krw(aggregate.saleTotal),
			note:
				aggregate.orderCount !== aggregate.eligibleCount
					? `${aggregate.eligibleCount} / ${aggregate.orderCount}건`
					: `${aggregate.eligibleCount}건`,
		},
		{
			label: '부가세 적립',
			value: krw(aggregate.vatTotal),
			note: aggregate.vatUnpaid > 0 ? `미납 ${krw(aggregate.vatUnpaid)}` : '납부 완료',
		},
		{
			label: '후원금 적립',
			value: krw(aggregate.donationTotal),
			note:
				aggregate.donationUnpaid > 0
					? `미납 ${krw(aggregate.donationUnpaid)}`
					: '납부 완료',
		},
		{
			label: '입금 대기',
			value: aggregate.awaitingCount > 0 ? `${aggregate.awaitingCount}건` : '0',
			note:
				aggregate.awaitingCount > 0
					? krw(aggregate.awaitingSale)
					: `세금계산서 ${aggregate.taxInvoiceCount} / ${
							aggregate.orderCount - aggregate.awaitingCount
					  }`,
		},
	]

	const loading =
		tab === 'year'
			? summaryStatus === 'loading' || summaryStatus === 'init'
			: rowsStatus === 'loading' || rowsStatus === 'init'
	const empty = tab === 'year' ? monthsKrw.length === 0 : rows.length === 0
	const hasSelection = selectedOrderIds.length > 0

	const picker =
		tab === 'year' ? (
			<FormControl>
				<InputLabel id="settlement-year-label">연도</InputLabel>
				<Select
					className="yearSelect"
					labelId="settlement-year-label"
					label="연도"
					value={year}
					size="small"
					onChange={(e: SelectChangeEvent) =>
						reduxDispatch(settlementActions.YEAR(e.target.value))
					}
				>
					{years.map(y => (
						<MenuItem key={y} value={y}>
							{y}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		) : tab === 'month' ? (
			<LocalizationProvider dateAdapter={AdapterDateFns}>
				<DatePicker
					className="monthPicker"
					label="월"
					views={['year', 'month']}
					openTo="month"
					value={new Date(`${month}-01`)}
					format="yyyy-MM"
					onChange={(v: Date | null) => {
						if (v !== null && dayjs(v).isValid())
							reduxDispatch(settlementActions.MONTH(dayjs(v).format('YYYY-MM')))
					}}
					slotProps={{
						textField: {
							variant: 'outlined',
							size: 'small',
							className: 'pickerField',
							placeholder: 'YYYY-MM',
						},
					}}
				/>
			</LocalizationProvider>
		) : (
			<View className="weekPickerView">
				<Pressable
					className="weekNavBtn"
					aria-label="지난주"
					onClick={() =>
						reduxDispatch(
							settlementActions.WEEK(dayjs(week).subtract(7, 'day').format('YYYY-MM-DD')),
						)
					}
				>
					<Icon name="chevron-left" />
				</Pressable>
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<DatePicker
						className="weekPicker"
						label="주 (아무 날)"
						value={new Date(week)}
						format="yyyy-MM-dd"
						onChange={(v: Date | null) => {
							if (v !== null && dayjs(v).isValid())
								reduxDispatch(settlementActions.WEEK(weekStartOf(v)))
						}}
						slotProps={{
							textField: {
								variant: 'outlined',
								size: 'small',
								className: 'pickerField',
								placeholder: 'YYYY-MM-DD',
							},
						}}
					/>
				</LocalizationProvider>
				<Pressable
					className="weekNavBtn"
					aria-label="다음주"
					onClick={() =>
						reduxDispatch(
							settlementActions.WEEK(dayjs(week).add(7, 'day').format('YYYY-MM-DD')),
						)
					}
				>
					<Icon name="chevron-right" />
				</Pressable>
			</View>
		)

	return (
		<View css={style}>
			<BoxView
				title="정산"
				className="settlementBoxView"
				leftHeaderAccessory={<View className="leftView">{picker}</View>}
				rightHeaderAccessory={
					tab !== 'year' && hasSelection && allAwaiting ? (
						<View className="rightView">
							{/* 도매 미입금 — 배송은 이미 진행 중일 수 있다. 입금 확인이 되면 정산 행이 서고 적립이 계산된다 */}
							<ActionButton
								title={`입금 확인 (${selectedOrderIds.length})`}
								iconName="cash-check"
								disabled={bulkSaving}
								onClick={onMarkPaid}
							/>
						</View>
					) : tab !== 'year' && hasSelection && noneAwaiting ? (
						<View className="rightView">
							<ActionButton
								title={`부가세 납부 처리 (${selectedOrderIds.length})`}
								iconName="check-circle-outline"
								disabled={bulkSaving}
								onClick={() => onBulk('vatPaidYn')}
							/>
							<ActionButton
								title={`후원금 납부 처리 (${selectedOrderIds.length})`}
								iconName="check-circle-outline"
								variant="outlined"
								disabled={bulkSaving}
								onClick={() => onBulk('donationPaidYn')}
							/>
						</View>
					) : tab !== 'year' && hasSelection ? (
						<span className="faint selectionHint">
							입금 대기와 결제 확정이 섞여 있습니다
						</span>
					) : null
				}
			>
				<Tabs
					value={tab}
					onChange={(_, next) => setTab(next as SettlementUnit)}
					className="unitTabs"
				>
					{UNITS.map(u => (
						<Tab key={u} label={UNIT_TITLE[u]} value={u} />
					))}
				</Tabs>

				{/* 기간의 큰 수 — 전부 원화 */}
				<View className="tilesView">
					<span className="boKicker">
						{periodTitle}
						{aggregate.hasUsd ? ' · USD 는 주문 시점 환율로 원화 환산' : ''}
					</span>
					{loading ? null : empty ? (
						<p className="boEmpty">{periodTitle}에는 정산할 판매가 없습니다.</p>
					) : (
						<BoStatTiles items={tiles} />
					)}
				</View>

				{tab === 'year' ? (
					/* 연간 — 월별 집계. 월간·주간의 판매 행과 같은 Grid 컴포넌트, 마지막 행이 합계 */
					<View className="rowsView">
						{/* key — 컬럼셋이 다른 그리드는 같은 인스턴스를 재사용하지 않는다(헤더가 옛 컬럼을 참조해 깨진다) */}
						<Grid
							key="year"
							title={`정산 — ${year}년 월별`}
							rows={yearRows}
							columns={settlementYearColumns}
							loading={loading}
							isEditable={false}
							checkboxSelection={false}
							sidePanel={false}
							toolbarVariant="search"
							hideFooter
							getRowClassName={(params: { row: { isTotal?: boolean } }) =>
								params.row.isTotal ? 'row-total' : ''
							}
							renderCell={{}}
						/>
					</View>
				) : (
					<View className="rowsView">
						<Grid
							key="rows"
							title={`정산 — ${periodTitle}`}
							rows={rows}
							columns={settlementColumns}
							loading={rowsStatus === 'loading'}
							isEditable={true}
							onAction={onAction}
							checkboxSelection={true}
							rowSelectionModel={selectedOrderIds}
							onRowSelectionModelChange={(model: GridRowSelectionModel) =>
								reduxDispatch(settlementActions.SELECTED_ORDER_IDS(model.map(String)))
							}
							renderCell={{
								channel: (params: GridRenderCellParams<SettlementRowType, any, any>) => (
									<span>{SETTLEMENT_CHANNEL_TITLE[params.row.channel]}</span>
								),
								status: (params: GridRenderCellParams<SettlementRowType, any, any>) => {
									const st = params.row.status
									if (st === 'sold')
										return <span className="faint">{SETTLEMENT_STATUS_TITLE[st]}</span>
									return (
										<span className={boStatusChipClass(SETTLEMENT_STATUS_CHIP[st])}>
											{SETTLEMENT_STATUS_TITLE[st]}
										</span>
									)
								},
								saleAmount: moneyCell,
								materialCost: (
									params: GridRenderCellParams<SettlementRowType, any, any>,
								) =>
									!params.row.hasSettlement ? (
										<span className="faint">—</span>
									) : (
										<span className="num">
											{moneyCell(params)}
											{params.row.costMissingQty > 0 && (
												<span
													className="boChip faint warnChip"
													title={`원가를 못 찾은 수량 ${params.row.costMissingQty} — 상품 관리 · 시즌 상품의 재료원가 칸을 채우면 다시 계산된다`}
												>
													원가 없음
												</span>
											)}
										</span>
									),
								shippingCost: moneyCell,
								dutyCost: moneyCell,
								vatReserve: settledMoneyCell,
								donationReserve: settledMoneyCell,
								vatPaidYn: checkCell('vatPaidYn'),
								donationPaidYn: checkCell('donationPaidYn'),
								taxInvoiceYn: checkCell('taxInvoiceYn'),
							}}
						/>
					</View>
				)}
			</BoxView>
		</View>
	)
}

export default SettlementPage

const style = css({
	...boShared,
	height: '100%',

	'.settlementBoxView': {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		// 본문이 셋(탭 · 타일 · 그리드) — 위아래로 쌓는다
		'.boxChildrenView': { flexDirection: 'column', alignItems: 'stretch', gap: 20 },
	},

	'.leftView': {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		'.yearSelect': { minWidth: '6rem' },
		'.pickerField': { width: '9rem' },
		'.weekPickerView': {
			display: 'flex',
			alignItems: 'center',
			gap: 2,
			'.weekNavBtn': {
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: 34,
				height: 34,
				borderRadius: 2,
				color: mono.dim,
				'&:hover': { color: mono.ink, background: mono.bg },
				'& .mdi': { fontSize: 22 },
			},
		},
	},
	'.rightView': { display: 'flex', alignItems: 'center', gap: 8 },
	'.selectionHint': { fontSize: 12 },

	'.unitTabs': {
		flex: '0 0 auto',
		minHeight: 40,
		borderBottom: `1px solid ${mono.line}`,
		'.MuiTab-root': {
			minHeight: 40,
			padding: '0 4px',
			marginRight: 24,
			fontWeight: 500,
			fontSize: 14,
		},
	},

	'.tilesView': {
		flex: '0 0 auto',
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
		'.boEmpty': { padding: '12px 0', textAlign: 'left' },
	},

	'.rowsView': {
		flex: '1 1 auto',
		minHeight: 280,
		display: 'flex',
		flexDirection: 'column',
		'& > *': { flex: '1 1 auto', minHeight: 0 },
		// 연간 합계 행 — 위 헤어라인 ink, 굵게
		'.MuiDataGrid-row.row-total .MuiDataGrid-cell': {
			fontWeight: 500,
			borderTop: `1px solid ${mono.ink}`,
		},
	},

	'.faint': { color: mono.faint },
	'.num': { fontVariantNumeric: 'tabular-nums' },
	'.warnChip': { marginLeft: 8 },
})
